import React, { useState, useEffect, useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { formatDate } from './utils';

const SidebarCustomerLicenses = ({ idClienteAkasia }) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();
  const [licenses, setLicenses] = useState([]);
  const [isLoadingLicenses, setIsLoadingLicenses] = useState(false);

  useEffect(() => {
    const fetchLicenses = async () => {
      if (!idClienteAkasia) {
        setLicenses([]);
        return;
      }
      setIsLoadingLicenses(true);
      try {
        const response = await fetch(`/api/SupportEngine/GetClientLicenses?idClienteAkasia=${idClienteAkasia}`, {
          headers: {
            'Accept': 'application/json',
            'Authorization': 'Bearer ' + token
          }
        });
        if (response.ok) {
          const result = await response.json();
          const list = result.data || result;
          setLicenses(Array.isArray(list) ? list : []);
        } else {
          setLicenses([]);
        }
      } catch (err) {
        console.error('Error al cargar licencias:', err);
        setLicenses([]);
      } finally {
        setIsLoadingLicenses(false);
      }
    };

    fetchLicenses();
  }, [idClienteAkasia, token]);

  return (
    <div>
      <div
        className="fw-semibold mb-2 pb-1 d-flex align-items-center justify-content-between"
        style={{ fontSize: '0.68rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b' }}
      >
        <span>LICENCIAS DEL CLIENTE {licenses.length > 0 && `(${licenses.length})`}</span>
        {isLoadingLicenses && (
          <span className="d-inline-flex align-items-center gap-1 text-primary fw-medium" style={{ fontSize: '0.65rem', textTransform: 'none' }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.62rem' }} />
            <span>Actualizando...</span>
          </span>
        )}
      </div>

      {!idClienteAkasia ? (
        <div
          className="p-2 rounded border text-center"
          style={{
            fontSize: '0.75rem',
            backgroundColor: isDark ? '#0b1727' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#64748b'
          }}
        >
          Asigna un cliente para ver sus licencias.
        </div>
      ) : isLoadingLicenses && licenses.length === 0 ? (
        <div className="text-center py-2 text-muted" style={{ fontSize: '0.75rem' }}>
          <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-primary" />
          Cargando licencias...
        </div>
      ) : licenses.length === 0 ? (
        <div
          className="p-2 rounded border text-center"
          style={{
            fontSize: '0.75rem',
            backgroundColor: isDark ? '#0b1727' : '#f8fafc',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#64748b'
          }}
        >
          No tiene licencias registradas.
        </div>
      ) : (
        <div
          className={`d-flex flex-column gap-2 pe-1 se-custom-scrollbar ${isDark ? 'se-dark-scrollbar' : ''}`}
          style={{
            maxHeight: '210px',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#2b394a transparent' : '#cbd5e1 transparent'
          }}
        >
          {licenses.map(lic => {
            const tipoDesc = (lic.vDescripcionTipo || '').toLowerCase();
            const isServidor = tipoDesc.includes('servidor');

            return (
              <div
                key={lic.idLicencia}
                className="p-2 rounded-2 border shadow-none"
                style={{
                  fontSize: '0.78rem',
                  backgroundColor: isDark ? '#121e2d' : '#ffffff',
                  borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
              >
                {/* Fila 1: Título de Licencia con ancho completo y link */}
                <div>
                  <a
                    href={`/licencias/detalle/${lic.idLicencia}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none hover-text-primary text-truncate d-block"
                    style={{ fontSize: '0.80rem', color: isDark ? '#f9fafd' : '#0f172a' }}
                    title={`Ver licencia #${lic.idLicencia} en nueva pestaña`}
                  >
                    #{lic.idLicencia} — {lic.vDescripcionTipo || 'Licencia'}
                  </a>
                  {lic.vNombreEmpresa && (
                    <div className={isDark ? 'text-400' : 'text-muted'} style={{ fontSize: '0.71rem', opacity: 0.85 }}>
                      {lic.vNombreEmpresa}
                    </div>
                  )}
                </div>

                {/* Fila 2: Póliza (Exclusivo para tipo 1 Servidor) */}
                {isServidor && (
                  <div
                    className="mt-1 pt-1 border-top d-flex align-items-center justify-content-between"
                    style={{ fontSize: '0.70rem', borderColor: isDark ? '#233142' : '#e2e8f0' }}
                  >
                    <span className={isDark ? 'text-400' : 'text-muted'}>Póliza:</span>
                    {lic.bTienePolizaActiva ? (
                      <span
                        className="d-inline-flex align-items-center gap-1 px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5',
                          color: isDark ? '#34d399' : '#065f46',
                          border: isDark ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid #a7f3d0',
                          fontSize: '0.67rem',
                          fontWeight: 500
                        }}
                      >
                        <span
                          style={{
                            width: '5px',
                            height: '5px',
                            borderRadius: '50%',
                            backgroundColor: '#10b981',
                            display: 'inline-block'
                          }}
                        />
                        Activa · Vence {formatDate(lic.dFechaVencimientoPoliza)}
                      </span>
                    ) : lic.dFechaVencimientoPoliza ? (
                      <span
                        className="d-inline-flex align-items-center gap-1 px-1.5 py-0.5 rounded"
                        style={{
                          backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fef2f2',
                          color: isDark ? '#f87171' : '#991b1b',
                          border: isDark ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #fecaca',
                          fontSize: '0.67rem',
                          fontWeight: 500
                        }}
                      >
                        <span
                          style={{
                            width: '5px',
                            height: '5px',
                            borderRadius: '50%',
                            backgroundColor: '#ef4444',
                            display: 'inline-block'
                          }}
                        />
                        Venció {formatDate(lic.dFechaVencimientoPoliza)}
                      </span>
                    ) : (
                      <span className={isDark ? 'text-400' : 'text-muted'} style={{ fontSize: '0.67rem' }}>Sin póliza</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SidebarCustomerLicenses;
